import { useNavigate } from 'react-router-dom';

import React from 'react';
import { reverse } from 'named-urls';
import { GridEventListener, GridRowParams, GridValidRowModel } from '@mui/x-data-grid';

import { formatMoment } from 'utils/formatters/timezone';

import routes from 'core/routes';
import { DATETIME_COLUMN_MIN_WIDTH, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR } from 'core/constants';

import Grid, { defaultFormatters, defaultOperators, defaultRenderers } from 'components/Grid';
import { ColumnDefinition } from 'components/Grid/types';

import { TaskStatus } from 'utils/api/modules/tasks/tasks.constants';

import { listRenderer } from 'components/Grid/renderers/GridListRenderer';
import { TaskStatusOperator } from 'components/Grid/operators/Status/';
import { ListRendererType } from 'components/Grid/renderers/GridListRenderer/types';

import { LinksType, UuidableSubject } from 'utils/api/types';

import { ExtendedUserOperators } from 'components/Grid/operators/User/User';

import { useStore } from 'utils/hooks/store';

import { getEntityGridColumns } from 'components/Grid/components/EntitiesGridColumn/EntitiesGridColumn';

import { Task } from 'utils/api/modules/tasks/tasks.types';

import TaskLinkedToCell from '../TaskLinkedToCell';
import { NotLinked } from '../TaskLinkedToCell/styles';
import { TasksTableProps } from './types';
import { StyledTaskChip, StyledTaskDueDate } from './styles';
import ActionCell from '../ActionCell';
import { ScheduleItem } from '../ActionsSchedulesTable/ActionsSchedulesTable';

function incidentEntity(value: UuidableSubject): LinksType {
  return {
    entity: 'incident',
    instance: {
      uuid: value.uuid,
      name: value.subject,
    },
    extraData: value,
  };
}

export const tasksTableBasicColumns = [
  {
    field: 'name',
    headerName: 'Task name',
    nativeFilter: true,
  },
  {
    defaultVisibility: false,
    field: 'description',
    headerName: 'Description',
    nativeFilter: true,
  },
  {
    field: 'customIncident',
    headerName: 'Linked to',
    renderCell: listRenderer(TaskLinkedToCell as unknown as ListRendererType, 1, undefined, {
      empty: <NotLinked>Not Linked</NotLinked>,
    }),
    valueGetter: (value: unknown, row: GridValidRowModel) =>
      value !== null ? [incidentEntity(value as UuidableSubject), ...row.links] : row.links,
    nativeFilter: 'linkedTo',
  },
  {
    field: 'assigned',
    headerName: 'Assigned to',
    renderCell: defaultRenderers.user,
    filterOperators: ExtendedUserOperators(),
    valueGetter: (value: unknown) => ({ value }),
    nativeFilter: true,
    // ToDo: implement sorting by user on backend
    sortable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    renderCell: ({ value }: { value: TaskStatus }) => <StyledTaskChip status={value} />,
    filterOperators: TaskStatusOperator(),
    nativeFilter: true,
  },
  {
    defaultVisibility: false,
    field: 'lastUpdate',
    headerName: 'Last update',
    minWidth: DATETIME_COLUMN_MIN_WIDTH,
    renderCell: ({ value }: { value: string }) => (
      <p data-testid="tasks-last-update">{formatMoment(value, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}</p>
    ),
    valueFormatter: defaultFormatters.date_time,
    filterOperators: defaultOperators.date_time(),
    nativeFilter: true,
  },
  {
    field: 'dueDate',
    headerName: 'Due date',
    renderCell: ({ value, row }: { value: string; row: Task }) => (
      <StyledTaskDueDate date={value} status={row.status} />
    ),
    filterOperators: defaultOperators.date(),
    nativeFilter: true,
  },
  {
    defaultVisibility: false,
    field: 'created',
    headerName: 'Created',
    minWidth: DATETIME_COLUMN_MIN_WIDTH,
    renderCell: ({ value }: { value: string }) => (
      <p data-testid="tasks-created-at">{formatMoment(value, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}</p>
    ),
    valueFormatter: defaultFormatters.date_time,
    filterOperators: defaultOperators.date_time(),
    nativeFilter: true,
  },
  {
    defaultVisibility: false,
    field: 'creator',
    headerName: 'Created by',
    renderCell: defaultRenderers.user,
    filterOperators: defaultOperators.user(),
    valueGetter: (value: unknown) => ({ value }),
    nativeFilter: true,
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: '',
    maxWidth: 50,
    ariaLabel: 'actions_cell',
    renderCell: ({ row, colDef }: { row: ScheduleItem; colDef: ColumnDefinition }) => (
      <ActionCell rowData={row} actions={colDef.actions} />
    ),
    filterOperators: null,
    sortable: false,
  },
] as unknown as ColumnDefinition[];

const TasksTable = ({ onError, onRefreshCallback }: TasksTableProps): React.ReactElement => {
  const navigate = useNavigate();
  const onRowClick: GridEventListener<'rowClick'> = (task: GridRowParams): void => {
    navigate(reverse(routes.dashboard.tasks.details, { taskId: task.row.uuid }));
  };

  const {
    session: { user },
  } = useStore();

  return (
    <Grid
      basicColumns={[...tasksTableBasicColumns, ...getEntityGridColumns(user?.activeEntityModules || [])]}
      filters={[
        { field: 'status', operator: 'is not', value: `-${TaskStatus.Completed}` },
        ...(user ? [{ field: 'assigned', operator: 'is', value: user.uuid }] : []),
      ]}
      entity="action"
      onError={onError}
      onRefreshCallback={onRefreshCallback}
      onRowClick={onRowClick}
      dataURL="/api/tasks/"
      sortBy={{ field: 'dueDate', sort: 'asc' }}
      customNoRowsText="No tasks have been created yet"
    />
  );
};

export default TasksTable;
